import { useEffect, useState } from "react";

// Single source of truth for the shell's viewport tiers (spec cat 5, rows 5.1/5.2/5.3).
// Replaces the two independent MOBILE_BREAKPOINT consts formerly duplicated in
// Sidebar.tsx and RightInspector.tsx — same numbers, one owner.
export const MOBILE_BREAKPOINT = 768;
export const DESKTOP_BREAKPOINT = 1024;

export type ResponsiveTier = "mobile" | "tablet" | "desktop";

function tierFromWidth(width: number): ResponsiveTier {
  if (width < MOBILE_BREAKPOINT) return "mobile";
  if (width < DESKTOP_BREAKPOINT) return "tablet";
  return "desktop";
}

// Debounced resize (the prior per-component listeners fired on every resize event with no
// throttle — fine for a single boolean flip, not fine once a resize can also flip persisted
// UI state like sidebar mode, so this hook debounces to avoid layout thrash while dragging).
const RESIZE_DEBOUNCE_MS = 80;

export function useResponsiveTier(): { tier: ResponsiveTier; isMobile: boolean } {
  const [tier, setTier] = useState<ResponsiveTier>(() =>
    typeof window === "undefined" ? "desktop" : tierFromWidth(window.innerWidth),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => setTier(tierFromWidth(window.innerWidth)), RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener("resize", onResize);
    return () => {
      if (timeout) clearTimeout(timeout);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return { tier, isMobile: tier === "mobile" };
}
